/*
 * <slide-show> (sections/slideshow.liquid).
 * The slides sit in a sideways-scrolling track that snaps one slide at a time, so swiping on
 * phones works without any code. This adds the arrows, the dots, and the optional autoplay
 * (which pauses while someone hovers, touches, uses the controls, or the tab is hidden).
 */
class SlideShow extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.dots = [...this.querySelectorAll('[data-dot]')];
    if (this.slides.length < 2) return;

    this.index = 0;
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.goTo(this.index - 1, 'smooth', true));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.goTo(this.index + 1, 'smooth', true));
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.goTo(Number(dot.dataset.dot), 'smooth', true)));

    // Keep the dots in step while swiping
    this.track.addEventListener('scroll', () => {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => {
        this.index = this.current();
        this.updateDots();
      }, 80);
    }, { passive: true });

    // Theme editor: jump to the slide being edited
    this.addEventListener('shopify:block:select', (event) => {
      this.paused = true;
      this.goTo(this.slides.indexOf(event.target), 'instant', true);
    });
    this.addEventListener('shopify:block:deselect', () => (this.paused = false));

    this.setupAutoplay();
  }

  current() {
    return Math.round(this.track.scrollLeft / this.track.clientWidth);
  }

  // byUser: someone pressed an arrow/dot, so restart the autoplay countdown
  goTo(index, behavior = 'smooth', byUser = false) {
    const count = this.slides.length;
    const target = (index + count) % count; // past the last slide -> back to the first, and vice versa
    this.index = target;
    if (byUser) this.lastAction = Date.now();
    this.track.scrollTo({ left: target * this.track.clientWidth, behavior });
    this.updateDots(target);
  }

  updateDots(index = this.current()) {
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
      dot.toggleAttribute('aria-current', i === index);
    });
  }

  setupAutoplay() {
    const delay = Number(this.dataset.autoplay);
    if (!delay || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const pause = () => (this.paused = true);
    const resume = () => (this.paused = false);
    this.addEventListener('mouseenter', pause);
    this.addEventListener('mouseleave', resume);
    this.addEventListener('focusin', pause);
    this.addEventListener('focusout', resume);
    // A touch (swipe, tap) stops autoplay for good: they're browsing the slides themselves
    this.addEventListener('touchstart', () => (this.stopped = true), { passive: true, once: true });

    this.lastAction = Date.now();
    setInterval(() => {
      if (this.paused || this.stopped || document.hidden) return;
      if (Date.now() - this.lastAction < delay) return; // someone just moved it: give them the full time
      const rect = this.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return; // off screen: don't move
      this.lastAction = Date.now();
      this.goTo(this.index + 1);
    }, 250);
  }
}

customElements.define('slide-show', SlideShow);
